import {
  Badge,
  Body,
  Button,
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  EmptyState,
  Separator,
  Title,
} from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import {
  faCopy,
  faEllipsisVertical,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { Link } from 'react-router-dom';
import { StationenPopover } from '@/domain/mitarbeiter/features/rotationsplan/features/Vorlagen/components/StationenPopover';
import {
  useRotationsplanVorlagen,
  type RotationsplanVorlage,
} from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlagen/useRotationsplanVorlagen';

const noop = () => {};

export function VorlagenUebersicht() {
  const { vorlagenByBeruf } = useRotationsplanVorlagen();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-start gap-6">
      {vorlagenByBeruf.length < 1 ? (
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="w-full h-full bg-backgrounds-primary rounded-xl p-8 flex justify-center items-center">
            <EmptyState
              icon={
                <FontAwesomeIcon
                  icon={faCopy}
                  className="text-neutrals-v2-700"
                />
              }
              title={t(
                'domain.mitarbeiter.rotationsplan.vorlagen.keinVorlage.title',
              )}
              description={t(
                'domain.mitarbeiter.rotationsplan.vorlagen.keinVorlage.subtitle',
              )}
            />
          </div>
        </div>
      ) : (
        vorlagenByBeruf.map((beruf) => (
          <section key={beruf.id} className="flex w-full flex-col gap-3">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faCircle}
                color={beruf.color}
                className="size-2.5"
              />
              <Title asChild>
                <h2>{beruf.name}</h2>
              </Title>
              <Body asChild className="text-labels-secondary">
                <span>{`· ${beruf.kuerzel}`}</span>
              </Body>
            </div>
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {beruf.vorlagen.map((vorlage) => (
                <VorlagenCard key={vorlage.id} vorlage={vorlage} />
              ))}
            </div>
          </section>
        ))
      )}
      <Button
        asChild
        iconOnly
        className="absolute right-4 bottom-16 z-49 shadow-xl md:hidden">
        <Link to={`/mitarbeiter/rotationsplaene/vorlagen/create`}>
          <FontAwesomeIcon icon={faPlus} />
        </Link>
      </Button>
    </div>
  );
}

function VorlagenCard({ vorlage }: { vorlage: RotationsplanVorlage }) {
  const { t } = useTranslation();

  return (
    // Der Titel-Link spannt sich über die ganze Karte, die Buttons liegen
    // per `relative z-10` darüber und bleiben einzeln klickbar.
    <Card className="relative h-full gap-3">
      <CardHeader>
        <CardTitle className="line-clamp-2">
          <Link
            to={`/mitarbeiter/rotationsplaene/vorlagen/${vorlage.id}`}
            className="after:absolute after:inset-0 after:rounded-[inherit]">
            <Body weight="semibold">{vorlage.titel}</Body>
          </Link>
        </CardTitle>
        <CardAction className="relative z-10">
          <Button
            aria-label={t(
              'domain.mitarbeiter.rotationsplan.vorlagen.mehrOptionen',
            )}
            iconOnly
            size="small"
            type="button"
            variant="ghost"
            onClick={noop}>
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-2">
        {vorlage.abschnitte.map((abschnitt) => (
          <section key={abschnitt.id} className="flex flex-col gap-1">
            <Body size="sm" className="text-labels-secondary">
              {`${abschnitt.position}. ${abschnitt.name}`}
            </Body>
            <div className="flex min-h-6 flex-wrap items-center gap-1.5">
              {abschnitt.arbeitsbereiche.map((arbeitsbereich) => (
                <Badge
                  key={`${abschnitt.id}-${arbeitsbereich.id}`}
                  dotColor={arbeitsbereich.color}
                  layout="character-dot"
                  size="sm">
                  {arbeitsbereich.name}
                </Badge>
              ))}
            </div>
          </section>
        ))}
      </CardContent>

      <Separator />

      <CardFooter className="justify-between">
        <StationenPopover vorlage={vorlage} />
        <Button
          className="relative z-10"
          type="button"
          variant="secondary-red"
          onClick={noop}>
          {t('domain.mitarbeiter.rotationsplan.vorlagen.anwenden')}
        </Button>
      </CardFooter>
    </Card>
  );
}
